import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { IconArrowSwipeLeft, IconArrowSwipeRight } from '../../../../../assets/images/icons';
import { ChartContainer, ArrowButtons, NumberLhs } from './style';

const SCROLL_AMOUNT = 300; 
const CHART_HEIGHT = 500;
const NUMBER_LHS_WIDTH = 100; // Fixed width of the Y‑axis label column

const D3FilteredChart = ({ data, totalPeriods }) => {
  // ─── Refs ──────────────────────────────────────────────────────────────────
  const chartScrollRef = useRef(null); 
  const chartRef = useRef(null);       

  // ─── State ─────────────────────────────────────────────────────────────────
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const MIN_VAL = -3;
  const MAX_VAL = 3;

  // ─── Helper: Sort quarters chronologically ────────────────────────────────
  const sortQuarters = (quartersArray) => {
    const parseQuarter = (q) => {
      const parts = q.split(' ');
      if (parts.length !== 2) return { year: 0, quarter: 0 };
      let year, quarter;
      if (parts[0][0] === 'Q') {
        quarter = parseInt(parts[0].substring(1), 10);
        year = parseInt(parts[1], 10);
      } else {
        year = parseInt(parts[0], 10);
        quarter = parseInt(parts[1].substring(1), 10);
      }
      return { year, quarter };
    };
    return [...quartersArray].sort((a, b) => {
      const aObj = parseQuarter(a);
      const bObj = parseQuarter(b);
      if (aObj.year !== bObj.year) return aObj.year - bObj.year;
      return aObj.quarter - bObj.quarter;
    });
  };

  // ─── Logic: Update Arrow Visibility ────────────────────────────────────────
  const updateArrows = useCallback(() => {
    const el = chartScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollPrev(scrollLeft > 5);
    setCanScrollNext(scrollLeft + clientWidth < scrollWidth - 5);
    setIsOverflowing(scrollWidth > clientWidth);
  }, []);

  // ─── D3 Draw Effect ────────────────────────────────────────────────────────
useEffect(() => {
    if (!data || data.length === 0) return;

    // 1. Data Processing - Filter out null values immediately
    const filteredData = data
      .map(brand => ({
        ...brand,
        // Only include points that have a non-null value and fall within bounds
        validPoints: (brand.scaleArray || []).filter(
          d => d.val !== null && 
               d.val !== undefined && 
               d.val >= MIN_VAL && 
               d.val <= MAX_VAL
        ),
      }))
      .filter(brand => brand.validPoints.length > 0);

    const rawQuarters = Array.from(
      new Set(filteredData.flatMap(d => d.periodArray || []))
    );
    const quarters = sortQuarters(rawQuarters);

    const brandsPerQuarter = {};
    quarters.forEach(q => { brandsPerQuarter[q] = 0; });

    filteredData.forEach(brand => {
      brand.validPoints.forEach(point => {
        if (brandsPerQuarter[point.periodLabel] !== undefined) {
          brandsPerQuarter[point.periodLabel]++;
        }
      });
    });

    // 2. Constants for Layout
    const HORIZONTAL_DISTANCE = 30;
    const EXTRA_PADDING = 40;
    const MIN_COL_WIDTH = 100;

    let colWidths = quarters.map(q =>
      Math.max(MIN_COL_WIDTH, (brandsPerQuarter[q] || 0) * HORIZONTAL_DISTANCE + EXTRA_PADDING)
    );

    // Force all columns to equal width
    const maxVal = Math.max(...colWidths);
    colWidths = colWidths.map(() => maxVal);

    // Scale columns to fill available width
    const margin = { top: 20, right: 30, bottom: 40, left: 50 };
    const containerWidth = chartScrollRef.current?.clientWidth || 0;
    if (containerWidth > 0) {
      const sumWidth = colWidths.reduce((a, b) => a + b, 0);
      const targetWidth = containerWidth - NUMBER_LHS_WIDTH - margin.left - margin.right;
      if (targetWidth > 0 && sumWidth < targetWidth) {
        const scale = targetWidth / sumWidth;
        colWidths = colWidths.map(w => w * scale);
      }
    }

    const colOffsets = colWidths.reduce((acc, w, i) => {
      acc.push(i === 0 ? 0 : acc[i - 1] + colWidths[i - 1]);
      return acc;
    }, []);

    const xCenter = (quarter) => {
      const idx = quarters.indexOf(quarter);
      return idx === -1 ? 0 : colOffsets[idx] + colWidths[idx] / 2;
    };

    const drawChart = () => {
      if (!chartRef.current || !chartScrollRef.current) return;
      d3.select(chartRef.current).selectAll('*').remove();

      const containerWidth = chartScrollRef.current.clientWidth;
      const calculatedWidth = (colOffsets[quarters.length - 1] || 0) + (colWidths[quarters.length - 1] || 0) + margin.left + margin.right;
      const totalWidth = Math.max(containerWidth, calculatedWidth);
      const chartWidth = totalWidth - margin.left - margin.right;
      const height = CHART_HEIGHT - margin.top - margin.bottom;

      d3.select(chartRef.current).style('width', `${totalWidth}px`);

      const svg = d3
        .select(chartRef.current)
        .append('svg')
        .attr('width', totalWidth)
        .attr('height', CHART_HEIGHT)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      const y = d3.scaleLinear()
        .domain([MIN_VAL, MAX_VAL])
        .range([height, 0])
        .clamp(true);

      // --- Draw alternating quarter-column backgrounds ---
      quarters.forEach((q, i) => {
        svg.append('rect')
          .attr('x', colOffsets[i])
          .attr('width', colWidths[i])
          .attr('y', 0)
          .attr('height', height)
          .attr('fill', i % 2 === 0 ? '#e9eef3' : '#ffffff')
          .attr('opacity', 0.35);
      });

      // Background zones
      const zones = [
        { y1: 1,  y2: 3,  c: '#d3f1e4' },
        { y1: -1, y2: 1,  c: '#dce0e3' },
        { y1: -3, y2: -1, c: '#fcd3d6' },
      ];
      zones.forEach(z => {
        svg.append('rect')
          .attr('x', 0)
          .attr('width', chartWidth)
          .attr('y', y(z.y2))
          .attr('height', Math.abs(y(z.y1) - y(z.y2)))
          .attr('fill', z.c);
      });

      // Horizontal grid lines
      svg.append('g')
        .call(d3.axisLeft(y).ticks(6).tickSize(-chartWidth).tickFormat(''))
        .selectAll('line')
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 1.5);

      // Vertical dividers
      for (let i = 0; i < quarters.length - 1; i++) {
        const xMid = colOffsets[i] + colWidths[i];
        svg.append('line')
          .attr('x1', xMid).attr('y1', 0)
          .attr('x2', xMid).attr('y2', height)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 2);
      }

      // X Axis (Quarters)
      const xAxisG = svg.append('g').attr('transform', `translate(0,${height + 15})`);
      quarters.forEach(q => {
        xAxisG.append('text')
          .attr('x', xCenter(q))
          .attr('y', 10)
          .attr('text-anchor', 'middle')
          .style('font-size', '13px')
          // .style('font-weight', '600')
          .text(q);
      });

      // Y Axis helper
      const yAxis = svg.append('g')
        .call(d3.axisLeft(y).ticks(6).tickSize(0).tickFormat(''));
      yAxis.select('.domain').remove();

      const tooltipGroup = svg.append('g').attr('class', 'tooltip-group');
      const sortedNames = filteredData.map(d => d.logoName);

      const getXOffset = (brandName, quarter) => {
        const brandsInCol = filteredData
          .filter(b => b.validPoints.some(p => p.periodLabel === quarter))
          .map(b => b.logoName);
        brandsInCol.sort((a, b) => sortedNames.indexOf(a) - sortedNames.indexOf(b));
        const idx = brandsInCol.indexOf(brandName);
        return idx === -1 ? 0 : (idx - (brandsInCol.length - 1) / 2) * HORIZONTAL_DISTANCE;
      };

      const lineGen = d3.line()
        .x(d => xCenter(d.periodLabel) + getXOffset(d.brandName, d.periodLabel))
        .y(d => y(d.val))
        .curve(d3.curveMonotoneX);

      // Render brands
      filteredData.forEach(brand => {
        const brandColor = '#333333';
        const brandValues = brand.validPoints.map(v => ({ ...v, brandName: brand.logoName }));
        const brandGroup = svg.append('g').attr('class', 'brand-group');

        // The line connects all "valid" (non-null) points
        if (brandValues.length > 1) {
          brandGroup.append('path')
            .datum(brandValues)
            .attr('class', 'brand-line')
            .attr('fill', 'none')
            .attr('stroke', brandColor)
            .attr('stroke-width', 1.2)
            .attr('stroke-dasharray', '4,3')
            .attr('d', lineGen);
        }

        brandValues.forEach(point => {
          const xPos = xCenter(point.periodLabel) + getXOffset(brand.logoName, point.periodLabel);
          const node = brandGroup.append('g').attr('transform', `translate(${xPos},${y(point.val)})`);

          const circle = node.append('circle')
            .attr('r', 12)
            .attr('fill', 'white')
            .attr('stroke', brandColor)
            .attr('stroke-width', 1.5)
            .style('cursor', 'pointer');

          const text = node.append('text')
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'central')
            .style('font-size', '9px')
            .attr('fill', brandColor)
            .text(t => {
              const t1 = brand.logoName.split(' ')
              let t2 = t1[0].substring(0, 1)
              if(t1.length > 1) t2 += t1[1].substring(0, 1)
              return t2.toUpperCase()
            })
            .style('cursor', 'pointer');

          // Tooltip interactions
          const hoverIn = function () {
            tooltipGroup.selectAll('*').remove();
            let tooltipY = y(point.val) - 45;
            if (tooltipY < 10) tooltipY = y(point.val) + 45;

            const tText = tooltipGroup.append('text')
              .attr('x', xPos).attr('y', tooltipY)
              .attr('text-anchor', 'middle')
              .style('font-size', '12px').style('fill', 'white')
              .text(brand.logoName);

            const bbox = tText.node().getBBox();
            tooltipGroup.insert('rect', 'text')
              .attr('x', bbox.x - 6).attr('y', bbox.y - 4)
              .attr('width', bbox.width + 12).attr('height', bbox.height + 8)
              .attr('fill', '#333').attr('rx', 4);

            svg.selectAll('.brand-group').style('opacity', 0.2);
            d3.select(this.parentNode.parentNode).style('opacity', 1)
              .select('.brand-line').attr('stroke-dasharray', '0').attr('stroke-width', 2);
          };

          const hoverOut = function () {
            tooltipGroup.selectAll('*').remove();
            svg.selectAll('.brand-group').style('opacity', 1);
            svg.selectAll('.brand-line').attr('stroke-dasharray', '4,3').attr('stroke-width', 1.2);
          };

          circle.on('mouseenter', hoverIn).on('mouseleave', hoverOut);
          text.on('mouseenter', hoverIn).on('mouseleave', hoverOut);
        });
      });

      setTimeout(updateArrows, 100);
    };

    drawChart();

    const handleResize = () => {
      drawChart();
      updateArrows();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [data, updateArrows]);


  const scrollToLeftFunc = () => {
    if (chartScrollRef.current) {
      chartScrollRef.current.scrollBy({
        left: 10000,
        behavior: 'instant'
      });
    }    
  }
  useEffect(() => {
    scrollToLeftFunc()
  }, [])
  useEffect(() => {
    scrollToLeftFunc()
  }, [data])

  // ─── Scroll Handlers ───────────────────────────────────────────────────────
  const handleScroll = (direction) => {
    if (chartScrollRef.current) {
      chartScrollRef.current.scrollBy({
        left: direction === 'next' ? SCROLL_AMOUNT : -SCROLL_AMOUNT,
        behavior: 'smooth'
      });
    }
  };

  return (
    <ChartContainer 
      className='d3LineGraphWrapper' 
      style={{ 
        position: 'relative', 
        width: '100%', 
        overflow: 'hidden', 
        maxWidth: window.innerWidth - 300 
      }}
    >
      <div
        ref={chartScrollRef}
        onScroll={updateArrows}
        style={{
          width: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
        className="hide-scrollbar"
      >
        <NumberLhs>
          <span>+3</span><span>+2</span><span>+1</span><span>0</span><span>-1</span><span>-2</span><span>-3</span>
        </NumberLhs>

        <div ref={chartRef} style={{ minWidth: '100%' }} />
      </div>

      {isOverflowing && (
        <>
          <ArrowButtons
            onClick={() => handleScroll('prev')}
            className='prev'
            style={{
              opacity: canScrollPrev ? 1 : 0,
              pointerEvents: canScrollPrev ? 'auto' : 'none',
              transition: 'opacity 0.2s ease'
            }}
          >
            <IconArrowSwipeLeft />
          </ArrowButtons>
          <ArrowButtons
            onClick={() => handleScroll('next')}
            className='next'
            style={{
              opacity: canScrollNext ? 1 : 0,
              pointerEvents: canScrollNext ? 'auto' : 'none',
              transition: 'opacity 0.2s ease'
            }}
          >
            <IconArrowSwipeRight />
          </ArrowButtons>
        </>
      )}
    </ChartContainer>
  );
};

export default D3FilteredChart;